// @ts-nocheck
/**
 * Data Center form — Domestic vs International branch, cascading
 * Country → State → City fetches, alpha-only contact person, digit-only
 * 10-char contact number, alnum+space company name.
 */
import { useState, useEffect } from 'react';
import {
  DATA_CENTER_TYPES,
  DATA_CENTER_DOMESTIC_TYPE,
  DATA_CENTER_INTERNATIONAL,
  DOMESTIC_COUNTRY,
} from '@/utilities/constant';
import { API_COUNTRY_CODES_LIST, API_STATES_LIST, API_CITIES_LIST } from '@/service/url';
import { postApiCall, getApiCall } from '@/service/httpCall';
import { validateEmail } from '@/utilities/helpers';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const Star = () => <span className="ml-0.5 text-destructive">*</span>;

function Field({ label, children, error }: any) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-foreground">
        {label} <Star />
      </label>
      {children}
      {error ? <div className="text-xs text-destructive">{error}</div> : null}
    </div>
  );
}

function RenderDataTypeFields({ props }: any) {
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);

  useEffect(() => {
    if (props.dataCenterType == DATA_CENTER_INTERNATIONAL) {
      getApiCall(API_COUNTRY_CODES_LIST, {})
        .then((response: any) => {
          const filtered = response.filter(
            (country: any) => country.name !== DOMESTIC_COUNTRY.value,
          );
          setCountries(filtered);
        })
        .catch((error) => console.error('Error while fetching countries:', error));
    }
  }, [props.dataCenterType]);

  useEffect(() => {
    if (props.country) {
      const params = { countryCode: props.country };
      postApiCall(API_STATES_LIST, params)
        .then((response: any) => setStates(response))
        .catch((error) => console.error('Error while fetching states:', error));
    }
  }, [props.country]);

  useEffect(() => {
    if (props.state && props.country) {
      const params = { countryCode: props.country, stateCode: props.state };
      postApiCall(API_CITIES_LIST, params)
        .then((response: any) => setCities(response))
        .catch((error) => console.error('Error while fetching cities:', error));
    }
  }, [props.state, props.country]);

  const pickCountry = (code: string) => {
    const opt = countries.find((c: any) => String(c.code) === code);
    props.setCountry(code);
    props.setCountryData((prev: any) => ({
      ...prev,
      [code]: opt?.name || code,
    }));
  };

  const pickState = (code: string) => {
    const opt = states.find((s: any) => String(s.isoCode) === code);
    props.setState(code);
    props.setStateData((prev: any) => ({
      ...prev,
      [code]: opt?.name || code,
    }));
  };

  if (props.dataCenterType !== '' && props.dataCenterType == DATA_CENTER_DOMESTIC_TYPE) {
    if (props.country !== DOMESTIC_COUNTRY.label) {
      // Keep the domestic-only side effect: fix country to the domestic label.
      props.setCountry(DOMESTIC_COUNTRY.label);
    }
    return (
      <>
        <Field label="State">
          <Select value={props.state || undefined} onValueChange={pickState}>
            <SelectTrigger className="h-10 w-full">
              <SelectValue placeholder="Select state…" />
            </SelectTrigger>
            <SelectContent>
              {states.map((val: any) => (
                <SelectItem key={val.isoCode} value={String(val.isoCode)}>
                  {val.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="City">
          <Select value={props.city || undefined} onValueChange={(v) => props.setCity(v)}>
            <SelectTrigger className="h-10 w-full">
              <SelectValue placeholder="Select city…" />
            </SelectTrigger>
            <SelectContent>
              {cities.map((val: any) => (
                <SelectItem key={val.name} value={String(val.name)}>
                  {val.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </>
    );
  }

  if (props.dataCenterType == DATA_CENTER_INTERNATIONAL) {
    return (
      <>
        <Field label="Country">
          <Select value={props.country || undefined} onValueChange={pickCountry}>
            <SelectTrigger className="h-10 w-full">
              <SelectValue placeholder="Select country…" />
            </SelectTrigger>
            <SelectContent>
              {countries.map((val: any) => (
                <SelectItem key={val.code} value={String(val.code)}>
                  {val.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="State">
          <Select value={props.state || undefined} onValueChange={pickState}>
            <SelectTrigger className="h-10 w-full">
              <SelectValue placeholder="Select state…" />
            </SelectTrigger>
            <SelectContent>
              {states.map((val: any) => (
                <SelectItem key={val.isoCode} value={String(val.isoCode)}>
                  {val.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="City">
          <Select value={props.city || undefined} onValueChange={(v) => props.setCity(v)}>
            <SelectTrigger className="h-10 w-full">
              <SelectValue placeholder="Select city…" />
            </SelectTrigger>
            <SelectContent>
              {cities.map((val: any) => (
                <SelectItem key={val.name} value={String(val.name)}>
                  {val.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </>
    );
  }
  return null;
}

export default function RenderDataCenterForm(props: any) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <Field label="Name" error={props.error}>
        <Input
          type="text"
          maxLength={100}
          value={props.name}
          placeholder="Name"
          onChange={props.handleChange}
        />
      </Field>

      <Field label="Data Center Type">
        <Select
          value={props.dataCenterType !== '' ? String(props.dataCenterType) : undefined}
          onValueChange={(v) => props.setDataCenterType(v)}
        >
          <SelectTrigger className="h-10 w-full">
            <SelectValue placeholder="Select data center type…" />
          </SelectTrigger>
          <SelectContent>
            {DATA_CENTER_TYPES.map((val: any) => (
              <SelectItem key={val.value} value={String(val.value)}>
                {val.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <RenderDataTypeFields props={props} />

      <Field label="Contact Person">
        <Input
          type="text"
          value={props.contactPerson}
          placeholder="Contact Person"
          maxLength={50}
          onChange={(e) => {
            const val = e.target.value;
            if (/^[A-Za-z]*$/.test(val)) {
              props.setContactPerson(val);
            }
          }}
        />
      </Field>

      <Field label="Contact Email" error={props.emailError}>
        <Input
          type="text"
          value={props.contactEmail}
          placeholder="Contact Email"
          onChange={(e) => {
            props.setContactEmail(e.target.value);
            props.setEmailError(validateEmail(e.target.value));
          }}
        />
      </Field>

      <Field label="Contact Number">
        <Input
          type="text"
          maxLength={10}
          value={props.contactNumber}
          placeholder="Contact Number"
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, '');
            if (val.length <= 10) {
              props.setContactNumber(val);
            }
          }}
        />
      </Field>

      <Field label="Data Center Company">
        <Input
          type="text"
          value={props.dataCenterCompany}
          placeholder="Data Center Company"
          maxLength={100}
          onChange={(e) => {
            const val = e.target.value;
            if (/^[A-Za-z0-9 ]*$/.test(val)) {
              props.setDataCenterCompany(val);
            }
          }}
        />
      </Field>

      <div className="md:col-span-2">
        <Field label="Data Center Company Address">
          <textarea
            rows={3}
            maxLength={500}
            value={props.dataCenterAddress}
            placeholder="Data Center Company Address"
            className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground shadow-[inset_2px_2px_5px_rgba(163,177,198,0.35),inset_-2px_-2px_5px_rgba(255,255,255,0.9)] outline-none focus:border-ring"
            onChange={(e) => props.setDataCenterAddress(e.target.value)}
          />
        </Field>
      </div>
    </div>
  );
}
